import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from 'firebase/firestore';
import { db } from '../firebase';
import {
  Booking,
  BookingInput,
  BookingStatus,
  isBranca,
} from '../types/booking';
import { isValidRange, rangesOverlap } from '../utils/slots';
import { isSuperAdmin } from './adminAuth';
import {
  syncBookingToGoogleCalendar,
  toCalendarSyncBooking,
} from './syncGoogleCalendar';

const COLLECTION = 'bookings';

function mapBooking(id: string, data: Record<string, unknown>): Booking {
  // Retrocompatibilità con il vecchio schema (date + timeSlot da 30 min)
  const legacyDate = data.date ? String(data.date) : '';
  const legacySlot = data.timeSlot ? String(data.timeSlot) : '';
  const legacyEnd =
    legacySlot &&
    (() => {
      const [h, m] = legacySlot.split(':').map(Number);
      const endM = m + 30;
      const hh = h + Math.floor(endM / 60);
      const mm = endM % 60;
      return `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
    })();

  return {
    id,
    gruppoScout: String(data.gruppoScout ?? ''),
    branca: (() => {
      const raw = String(data.branca ?? '');
      return isBranca(raw) ? raw : '';
    })(),
    numeroRagazzi: Number(data.numeroRagazzi ?? 0),
    numeroAdulti: Number(data.numeroAdulti ?? 0),
    referenteNome: String(data.referenteNome ?? ''),
    telefono: String(data.telefono ?? ''),
    email: String(data.email ?? ''),
    startDate: String(data.startDate ?? legacyDate),
    endDate: String(data.endDate ?? legacyDate),
    startTime: String(data.startTime ?? legacySlot),
    endTime: String(data.endTime ?? legacyEnd ?? ''),
    altreInfo: String(data.altreInfo ?? ''),
    status: (data.status as BookingStatus) ?? 'pending',
    googleCalendarEventId: data.googleCalendarEventId
      ? String(data.googleCalendarEventId)
      : undefined,
    createdAt: typeof data.createdAt === 'number' ? data.createdAt : Date.now(),
    updatedAt: typeof data.updatedAt === 'number' ? data.updatedAt : Date.now(),
  };
}

/**
 * Sync calendario in background (Apps Script).
 * Non await: l’utente non deve aspettare il calendar di service.
 */
function syncCalendarSafe(
  booking: Parameters<typeof syncBookingToGoogleCalendar>[0]
): void {
  void syncBookingToGoogleCalendar(booking).catch((err) => {
    console.error('Sync Google Calendar non riuscito:', err);
  });
}

export async function getAllBookings(): Promise<Booking[]> {
  const q = query(collection(db, COLLECTION), orderBy('createdAt', 'desc'));
  const snap = await getDocs(q);
  return snap.docs.map((d) => mapBooking(d.id, d.data()));
}

export async function getApprovedBookings(): Promise<Booking[]> {
  const q = query(
    collection(db, COLLECTION),
    where('status', '==', 'approved')
  );
  const snap = await getDocs(q);
  return snap.docs
    .map((d) => mapBooking(d.id, d.data()))
    .sort(
      (a, b) =>
        a.startDate.localeCompare(b.startDate) ||
        a.startTime.localeCompare(b.startTime)
    );
}

/** Solo le approvate occupano lo slot; le pending possono sovrapporsi tra loro. */
export async function findApprovedOverlaps(
  range: Pick<Booking, 'startDate' | 'endDate' | 'startTime' | 'endTime'>,
  excludeId?: string
): Promise<Booking[]> {
  const approved = await getApprovedBookings();
  return approved.filter(
    (b) => b.id !== excludeId && rangesOverlap(b, range)
  );
}

export async function createBooking(input: BookingInput): Promise<string> {
  if (!isValidRange(input)) {
    throw new Error('L’intervallo data/ora non è valido.');
  }

  const now = Date.now();
  const ref = await addDoc(collection(db, COLLECTION), {
    ...input,
    status: 'pending' as BookingStatus,
    createdAt: now,
    updatedAt: now,
    createdAtServer: serverTimestamp(),
  });

  syncCalendarSafe({
    id: ref.id,
    ...input,
    status: 'pending',
  });

  return ref.id;
}

/**
 * Carica le prenotazioni con getDocs (niente Listen/WebChannel).
 * Gli adblocker spesso bloccano Firestore Listen → ERR_BLOCKED_BY_CLIENT.
 */
export function pollBookings(
  onData: (bookings: Booking[]) => void,
  onError?: (error: Error) => void,
  intervalMs = 15000
): () => void {
  let cancelled = false;

  const load = async () => {
    try {
      const data = await getAllBookings();
      if (!cancelled) onData(data);
    } catch (err) {
      if (!cancelled) {
        onError?.(
          err instanceof Error
            ? err
            : new Error('Errore nel caricamento delle prenotazioni.')
        );
      }
    }
  };

  load();
  const timer = window.setInterval(load, intervalMs);
  return () => {
    cancelled = true;
    window.clearInterval(timer);
  };
}

export function pollApprovedBookings(
  onData: (bookings: Booking[]) => void,
  onError?: (error: Error) => void,
  intervalMs = 20000
): () => void {
  let cancelled = false;

  const load = async () => {
    try {
      const data = await getApprovedBookings();
      if (!cancelled) onData(data);
    } catch (err) {
      if (!cancelled) {
        onError?.(
          err instanceof Error
            ? err
            : new Error('Errore nel caricamento del calendario.')
        );
      }
    }
  };

  load();
  const timer = window.setInterval(load, intervalMs);
  return () => {
    cancelled = true;
    window.clearInterval(timer);
  };
}

export async function updateBookingStatus(
  id: string,
  status: BookingStatus,
  booking?: Booking,
  options?: { force?: boolean }
): Promise<{ conflicts?: Booking[] }> {
  // Conflitto solo in approvazione (salvo force = Approva comunque).
  if (status === 'approved' && booking && !options?.force) {
    const conflicts = await findApprovedOverlaps(booking, id);
    if (conflicts.length > 0) {
      return { conflicts };
    }
  }

  await updateDoc(doc(db, COLLECTION, id), {
    status,
    updatedAt: Date.now(),
  });

  if (booking) {
    syncCalendarSafe({
      ...toCalendarSyncBooking({ ...booking, status }),
    });
  }

  return {};
}

/** Aggiorna i dati di una prenotazione (admin). */
export async function updateBooking(
  id: string,
  input: BookingInput,
  currentStatus: BookingStatus,
  googleCalendarEventId?: string
): Promise<void> {
  if (!isValidRange(input)) {
    throw new Error('L’intervallo data/ora non è valido.');
  }

  await updateDoc(doc(db, COLLECTION, id), {
    ...input,
    updatedAt: Date.now(),
  });

  syncCalendarSafe({
    id,
    ...input,
    status: currentStatus,
    googleCalendarEventId,
  });
}

/** Elimina una prenotazione. Solo se cookie superAdmin=true (controllo UI + client). */
export async function deleteBooking(booking: Booking): Promise<void> {
  if (!isSuperAdmin()) {
    throw new Error('Operazione non consentita.');
  }

  await deleteDoc(doc(db, COLLECTION, booking.id));

  // Rimuove l’evento Google in background
  syncCalendarSafe({
    ...toCalendarSyncBooking({ ...booking, status: 'rejected' }),
  });
}
